import * as THREE from 'three';
import { SHIP_COLORS, type ShipColor, type ShipSpec } from './ShipCatalog';
import { buildFrigate, type FrigateModel } from './procedural/FrigateBuilder';
import { markForeground } from '../world/foreground';

let glowTexture: THREE.Texture | null = null;
function getGlowTexture(): THREE.Texture {
  if (glowTexture) return glowTexture;
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.18, 'rgba(255,255,255,0.85)');
  grad.addColorStop(0.45, 'rgba(255,255,255,0.25)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  glowTexture = new THREE.CanvasTexture(c);
  glowTexture.colorSpace = THREE.SRGBColorSpace;
  return glowTexture;
}

let registryCounter = 1;

/** A procedural frigate with engine plumes and navigation lights; used by the player, remotes and the menu. */
export class ShipVisual {
  readonly group = new THREE.Group();
  readonly model: FrigateModel;
  private readonly plumes: THREE.Sprite[] = [];
  private readonly plumeCore: THREE.Mesh[] = [];
  private flicker = 0;
  private time = Math.random() * 10;
  readonly ready: Promise<void> = Promise.resolve();

  constructor(readonly spec: ShipSpec, color: ShipColor, registry?: string) {
    const reg = registry ?? `SV-${String(registryCounter++).padStart(3, '0')}`;
    this.model = buildFrigate({ ...spec.frigate, length: spec.length, registry: `${reg} ${spec.name.toUpperCase()}` }, SHIP_COLORS[color] ?? SHIP_COLORS.blue);
    markForeground(this.model.root);
    this.group.add(this.model.root);
    const engineColor = new THREE.Color(spec.engineColor);
    for (const e of this.model.engines) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: getGlowTexture(), color: engineColor.clone().multiplyScalar(1.6), blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }),
      );
      sprite.position.copy(e.position);
      sprite.renderOrder = 11;
      sprite.userData.radius = e.radius;
      this.plumes.push(sprite);
      this.group.add(sprite);
      const core = new THREE.Mesh(
        new THREE.ConeGeometry(e.radius * 0.75, e.radius * 6, 20, 1, true),
        new THREE.MeshBasicMaterial({ color: engineColor.clone().multiplyScalar(1.4), transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, depthWrite: false }),
      );
      core.rotation.x = -Math.PI / 2;
      core.userData.base = e.position.clone();
      core.userData.radius = e.radius;
      core.renderOrder = 11;
      this.plumeCore.push(core);
      this.group.add(core);
    }
  }

  get size(): THREE.Vector3 {
    return this.model.size;
  }

  setThrust(throttle: number, boosting: boolean, dt: number): void {
    this.flicker += dt * 40;
    this.time += dt;
    const t = Math.max(0.12, Math.abs(throttle)) * (boosting ? 1.7 : 1);
    const f = 0.9 + Math.sin(this.flicker) * 0.06 + Math.sin(this.flicker * 2.7) * 0.04;
    for (const p of this.plumes) p.scale.setScalar(p.userData.radius * (1.6 + t * 2.2) * f);
    for (const c of this.plumeCore) {
      const r = c.userData.radius as number;
      const len = 0.3 + t * 1.8 * f;
      c.scale.set(1, len, 1);
      c.position.copy(c.userData.base).setZ(c.userData.base.z + r * 3 * len);
      (c.material as THREE.MeshBasicMaterial).opacity = 0.12 + t * 0.4;
    }
    // Navigation lights: steady with a slow pulse; strobes flash twice every 1.6 s.
    const pulse = 0.75 + 0.25 * Math.sin(this.time * 3);
    for (const n of this.model.navLights) (n.material as THREE.SpriteMaterial).opacity = pulse;
    const ph = this.time % 1.6;
    const on = ph < 0.06 || (ph > 0.16 && ph < 0.22);
    for (const s of this.model.strobes) s.visible = on;
  }

  /** Cockpit view hides the bridge tower (the camera sits inside it) but keeps the hull in sight. */
  setCockpitMode(cockpit: boolean): void {
    this.model.bridge.visible = !cockpit;
    for (const p of this.plumes) p.visible = !cockpit;
  }

  setModelVisible(v: boolean): void {
    this.group.visible = v;
  }
}
